import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import DOMPurify from 'dompurify';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { InternalMeetingNoteType } from '../../../../models/AdminModels';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { useNoteByIdQuery } from '../../../../queries/Note/useNoteByIdQuery';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components-v2/Button';
import { CalendarIcon } from '../../../../web/components/svgs/icons/CalendarIcon';
import { EditItemPenIcon } from '../../../../web/components/svgs/icons/EditItemPenIcon';
import { NotesIcon } from '../../../../web/components/svgs/icons/NotesIcon';
import { success } from '../../../../web/styles/colors';
import { AccountNoteComposer } from './AccountNoteComposer';
import { accountNoteStyles as s } from './accountNoteStyles';
import { MeetingNoteComposer } from './MeetingNoteComposer';

/** Legacy copy shown when an editable (Sales/Summary) note is missing. */
const CLICK_TO_ADD = 'Click the edit icon to the right to add a note.';
/** Legacy empty copy for non-editable note types (CSM). */
const NO_NOTE = 'No note';

export interface IAccountNoteProps {
	/** The persisted account; note ids are read off `account.additionalInfo`. */
	account: Api.IAccount;
	/** Which internal note this card displays. */
	type: InternalMeetingNoteType;
	/** Section title (e.g. "Summary from Sales"). */
	title: string;
	/** Render the CSM "Add a Note" action (gated by `AnnotateAccount`). */
	showAddActions?: boolean;
}

/** Pulls the note id for `type` off the account's additionalInfo. */
const noteIdFor = (account: Api.IAccount, type: InternalMeetingNoteType): string | undefined => {
	const info = account.additionalInfo;
	switch (type) {
		case InternalMeetingNoteType.CSM:
			return info?.csmNoteId ?? undefined;
		case InternalMeetingNoteType.Sales:
			return info?.salesNoteId ?? undefined;
		case InternalMeetingNoteType.Summary:
			return info?.summaryNoteId ?? undefined;
		default:
			return undefined;
	}
};

/** Resolve a note's content to the inner HTML used for read-only display + composer seeding. */
const noteHtml = (note?: Api.INote): string =>
	note ? (Api.getContentHtmlStringValueFromRawRichTextContentState(note.content) ?? '') : '';

/**
 * Account Overview single note (Sales / Summary / CSM). No MobX — reads the note id off the
 * plain account, fetches it via the react-query `useNoteByIdQuery`, and renders the author, the
 * last-modified date, and the note body as read-only sanitized HTML (converted from the note's raw
 * rich-text content state). When the note id is absent it shows a plain prompt to add one.
 * The CSM card optionally exposes the "Add a Note" action, gated by `AnnotateAccount`, that opens
 * the no-MobX {@link AccountNoteComposer} (TipTap editor + react-query save).
 */
export const AccountNote = ({ account, type, title, showAddActions }: IAccountNoteProps) => {
	const canAnnotate = useAdminUserSession().hasPermission(AdminPermission.AnnotateAccount);
	const noteId = noteIdFor(account, type);
	const { data: note, isError } = useNoteByIdQuery(noteId);

	// `null` = closed. 'add' opens a blank composer for a new note (the CSM "Add a Note" action);
	// 'edit' reopens the displayed note for in-place editing (the Sales / Summary pencil).
	const [composerMode, setComposerMode] = useState<'add' | 'edit' | null>(null);
	const [isMeetingComposerOpen, setIsMeetingComposerOpen] = useState(false);

	// Collapsed by default, like legacy. Legacy showed the server-truncated `preview` string rather
	// than visually clamping the rendered body, which keeps the collapsed size consistent across notes
	// no matter what font sizes their HTML carries.
	const [isExpanded, setIsExpanded] = useState(false);

	const html = useMemo(() => noteHtml(note), [note]);
	// Notes render as raw HTML, so sanitize with DOMPurify before injecting: legacy-sourced or
	// API-crafted content could otherwise carry <script>, inline handlers, or javascript: URLs.
	const sanitizedHtml = useMemo(() => DOMPurify.sanitize(html), [html]);

	// Legacy gated its toggle on `preview` alone, which left a "Read More" on notes short enough that
	// the preview was the whole note. Also require there to be more text to actually reveal.
	const preview = note?.preview ?? '';
	const hasMoreThanPreview = !!preview && (note?.plainTextContent?.length ?? 0) > preview.length;

	// Notes taken through "Add Meeting Notes" carry a meeting context; legacy appends
	// "<name> (<status>)" to the meta row so the note says which meeting it came from.
	const noteContext = note?.context as Api.IMeetingContext | undefined;
	const meetingContext = noteContext?.name && noteContext.meetingStatus ? noteContext : undefined;

	const isEditableType = type === InternalMeetingNoteType.Sales || type === InternalMeetingNoteType.Summary;

	return (
		<div className={css(s.container)}>
			<div className={css(s.header)}>
				<span className={css(s.title)}>{title}</span>
				{canAnnotate &&
					(showAddActions ? (
						<div className={css(s.actions)}>
							<Button variant='link' styles={[s.actionButton]} onClick={() => setComposerMode('add')}>
								<NotesIcon fillColor={success} />
								<span>Add a Note</span>
							</Button>
							<Button variant='link' styles={[s.actionButton]} onClick={() => setIsMeetingComposerOpen(true)}>
								<CalendarIcon width={16} height={16} style={{ color: success }} />
								<span>Add Meeting Notes</span>
							</Button>
						</div>
					) : isEditableType ? (
						// Sales / CSM-summary notes expose an inline pencil (matching legacy) that opens the composer.
						<Button
							variant='link'
							styles={[s.actionButton]}
							onClick={() => setComposerMode('edit')}
							aria-label={`Edit ${title}`}
						>
							<EditItemPenIcon />
						</Button>
					) : null)}
			</div>

			{isError ? (
				<span className={css(s.errorState)}>Error loading note</span>
			) : note ? (
				<>
					<div className={css(s.meta)}>
						<span>{getDisplayName(note.creator)}</span>
						<span>{moment(note.lastModifiedDate).format('MMM DD, YYYY hh:mm')}</span>
						{meetingContext && (
							<>
								<span>-</span>
								<span>{`${meetingContext.name} (${meetingContext.meetingStatus})`}</span>
							</>
						)}
					</div>
					{!isExpanded && hasMoreThanPreview ? (
						// Collapsed: the plain-text preview, so no note HTML is injected on this path at all.
						<div className={css(s.content)}>{`${preview}...`}</div>
					) : (
						/* Read-only display: the HTML is sanitized above with DOMPurify. */
						<div className={css(s.content)} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
					)}
					{hasMoreThanPreview && (
						<Button variant='link' styles={[s.expandToggle]} onClick={() => setIsExpanded(current => !current)}>
							{isExpanded ? 'Show Less' : 'Read More'}
						</Button>
					)}
				</>
			) : (
				<span className={css(s.emptyState)}>{isEditableType ? CLICK_TO_ADD : NO_NOTE}</span>
			)}

			{/*
			 * Mount the composer only while open so it always seeds the TipTap editor from the CURRENT
			 * note HTML. Keeping it mounted captured `initialHtml` on first render (before the note query
			 * resolved), so opening it later showed an empty editor and Save wiped the existing note.
			 */}
			{composerMode && (
				<AccountNoteComposer
					accountId={account.id}
					type={type}
					title={title}
					mode={composerMode}
					initialHtml={html}
					noteId={noteId}
					nextMeeting={account.additionalInfo?.nextMeeting}
					isOpen
					onClose={() => setComposerMode(null)}
				/>
			)}

			{isMeetingComposerOpen && (
				<MeetingNoteComposer account={account} isOpen onClose={() => setIsMeetingComposerOpen(false)} />
			)}
		</div>
	);
};
